import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { formatDate } from '../lib/formatUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Bell, Send, Shield, Globe, Server, FileText, MessageSquare, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const typeIcons = { Domain: Globe, Hosting: Server, SSL: Shield, AMC: FileText, DLT: MessageSquare };
const urgencyBadge = (days) => {
  if (days <= 7) return <Badge className="bg-red-500/15 text-red-600 border-red-500/30">{days}d left</Badge>;
  if (days <= 15) return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">{days}d left</Badge>;
  return <Badge className="bg-green-500/15 text-green-600 border-green-500/30">{days}d left</Badge>;
};

export const RenewalAlertsPage = () => {
  const [config, setConfig] = useState({ enabled: false, days_before: [30, 15, 7], recipients: '' });
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, previewRes] = await Promise.all([
        api.get('/renewals/config'),
        api.get('/renewals/preview'),
      ]);
      if (configRes.data.success) setConfig(configRes.data.data);
      if (previewRes.data.success) setRenewals(previewRes.data.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/renewals/config', config);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/renewals/send');
      if (res.data.success) {
        toast.success(res.data.message);
        fetchData();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) { toast.error('Failed to send notifications'); }
    setSending(false);
  };

  const criticalCount = renewals.filter(r => r.days_left <= 7).length;
  const warningCount = renewals.filter(r => r.days_left > 7 && r.days_left <= 15).length;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="renewal-alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Renewal Alerts</h1>
          <p className="text-muted-foreground">Configure and send automated renewal notifications</p>
        </div>
        <Button data-testid="send-now-btn" onClick={handleSendNow} disabled={sending || renewals.length === 0}>
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          {sending ? 'Sending...' : 'Send Alerts Now'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Bell className="h-8 w-8 text-primary" /><div><p className="text-2xl font-bold">{renewals.length}</p><p className="text-sm text-muted-foreground">Total Upcoming</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><AlertTriangle className="h-8 w-8 text-red-500" /><div><p className="text-2xl font-bold">{criticalCount}</p><p className="text-sm text-muted-foreground">Critical (7 days)</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><AlertTriangle className="h-8 w-8 text-amber-500" /><div><p className="text-2xl font-bold">{warningCount}</p><p className="text-sm text-muted-foreground">Warning (15 days)</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><CheckCircle2 className="h-8 w-8 text-green-500" /><div><p className="text-2xl font-bold">{renewals.length - criticalCount - warningCount}</p><p className="text-sm text-muted-foreground">Normal</p></div></div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Alert Settings</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <Label htmlFor="enabled">Enable Alerts</Label>
              <Switch id="enabled" data-testid="renewal-enabled-toggle" checked={config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} />
            </div>
            <div className="space-y-2">
              <Label>Notification Window (days before expiry)</Label>
              <Input data-testid="renewal-days" value={config.days_before?.join(', ') || '30, 15, 7'} onChange={e => setConfig({ ...config, days_before: e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d)) })} placeholder="30, 15, 7" />
              <p className="text-xs text-muted-foreground">Comma-separated values. Largest value determines preview window.</p>
            </div>
            <div className="space-y-2">
              <Label>Recipient Emails</Label>
              <Input data-testid="renewal-recipients" value={config.recipients} onChange={e => setConfig({ ...config, recipients: e.target.value })} placeholder="admin@company.com, finance@company.com" />
              <p className="text-xs text-muted-foreground">Comma-separated email addresses.</p>
            </div>
            {config.last_sent && <p className="text-xs text-muted-foreground">Last sent: {formatDate(config.last_sent)}</p>}
            <Button data-testid="save-renewal-config" className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Upcoming Renewals Preview</CardTitle></CardHeader>
          <CardContent>
            {renewals.length === 0 ? <p className="text-center py-8 text-muted-foreground">No upcoming renewals within the configured window.</p> :
            <Table>
              <TableHeader><TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead>Urgency</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {renewals.map((r, i) => {
                  const Icon = typeIcons[r.type] || FileText;
                  return (
                    <TableRow key={i} data-testid={`renewal-row-${i}`}>
                      <TableCell><div className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" />{r.type}</div></TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.client || '-'}</TableCell>
                      <TableCell>{formatDate(r.expiry_date)}</TableCell>
                      <TableCell>{urgencyBadge(r.days_left)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
